import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AppLayout from "./AppLayout";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Phone, MapPin, Clock, CheckCircle, AlertCircle, IndianRupee } from "lucide-react";
import authService from "../Services/authService";
import { useLocation } from "react-router-dom";

const AppointmentsScreen = () => {
  const location = useLocation();
  const defaultTab = location.state?.defaultTab || "today";
  const [filter, setFilter] = useState(defaultTab);
  const [data, setData] = useState({
    today: [],
    upcoming: [],
    past: [],
  });

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = () => {
    const token = authService.getCurrentUsertoken();
    const url = new URL(authService.getApiUrl() + "phlebotomistorders");

    fetch(url, {
      method: "POST",
      body: JSON.stringify({
        assign_to: authService.getCurrentuser().id,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        const updatedData = {
          today: result?.data?.today || [],
          upcoming: result?.data?.upcoming || [],
          past: result?.data?.past || [],
        };
        setData(updatedData);
      })
      .catch((err) => {
        console.error("Error loading appointments:", err.message);
      });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return (
          <span className="flex items-center text-xs font-medium text-amber-600">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
      case "In-progress":
        return (
          <span className="flex items-center text-xs font-medium text-blue-600">
            <AlertCircle className="h-3 w-3 mr-1" />
            In Progress
          </span>
        );
      case "Completed":
        return (
          <span className="flex items-center text-xs font-medium text-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </span>
        );
      default:
        return null;
    }
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return "";
    const [hour, minute] = time24.split(":");
    const h = parseInt(hour);
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minute} ${suffix}`;
  };

  const formatFullAddress = (appt) => {
    const parts = [
      appt.building_name,
      appt.landmark,
      appt.area,
      appt.city,
      appt.state,
      appt.pincode
    ];

    return parts.filter(Boolean).join(", ").replace(/\s*,\s*/g, ", ").trim();
  };

  const handleNavigateLocation = (address) => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    // Show loading indicator or alert (optional)
    console.log("Attempting to get current location...");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (!latitude || !longitude) {
          alert("Failed to detect your location.");
          return;
        }

        const origin = `${latitude},${longitude}`;
        const destination = encodeURIComponent(address.trim());

        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;

        console.log("Opening Google Maps URL:", url);
        // ✅ Make sure it's allowed to open
        const newWindow = window.open(url, "_blank");
        if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
          alert("Please allow popups to open Google Maps.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);

        let errorMessage = "Location error occurred.";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission was denied.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location position is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
        }

        alert(errorMessage + " Showing destination only.");

        // Fallback: open Maps without origin
        const destination = encodeURIComponent(address.trim());
        const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
        window.open(fallbackUrl, "_blank");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };


  return (
    <AppLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center">
          <div className="relative h-[100px] w-[100px] mr-3 flex-shrink-0">
            <img
              src="/images/pranaam-365-logo.png"
              alt="Pranaam365 Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-xl font-bold text-gray-800">Appointments</h1>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="today" onValueChange={setFilter} value={filter}>
          <TabsList className="grid w-full grid-cols-3 h-13 p-1 pt-0 bg-gray-100 rounded-md overflow-hidden">
            <TabsTrigger
              value="today"
              className="h-full w-full flex items-center justify-center text-sm text-gray-700
               data-[state=active]:bg-pranaam-red
               data-[state=active]:text-white
               transition-all duration-150"
            >
              Today
            </TabsTrigger>
            <TabsTrigger
              value="upcoming"
              className="h-full w-full flex items-center justify-center text-sm text-gray-700
               data-[state=active]:bg-pranaam-red
               data-[state=active]:text-white
               transition-all duration-150"
            >
              Upcoming
            </TabsTrigger>
            <TabsTrigger
              value="past"
              className="h-full w-full flex items-center justify-center text-sm text-gray-700
               data-[state=active]:bg-pranaam-red
               data-[state=active]:text-white
               transition-all duration-150"
            >
              Past
            </TabsTrigger>
          </TabsList>

        </Tabs>

        {/* Appointments List */}
        <div className="space-y-3">
          {data[filter]?.length > 0 ? (
            data[filter].map((appointment) => (
              <Card
                key={appointment.id}
                className="overflow-hidden border-gray-200"
              >
                <div className="p-3">
                  <div className="flex justify-between items-start mb-2 text-left">
                    <div className="flex-1 min-w-0 mr-2">
                      <h3 className="font-medium text-gray-800 truncate">
                        {appointment?.orders_items?.familyMembers || "Patient"}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {appointment?.orders_items?.test_name || "Test Info"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-pranaam-red text-white flex-shrink-0">
                        {filter === "today"
                          ? formatTime12Hour(appointment.slot_time)
                          : `${appointment.slot_date} ${formatTime12Hour(appointment.slot_time)}`}
                      </span>

                    </div>
                  </div>

                  <div className="flex text-sm text-gray-500 mb-3">
                    <MapPin className="h-3 w-3 mr-1 flex-shrink-0 mt-1" />
                    <span className="text-left" >
                      {appointment.building_name},{appointment.area},{" "}
                      {appointment.landmark},{appointment.pincode}
                    </span>
                  </div>

                  {Number(appointment.due_amount) > 0 && (
                    <div className="flex items-center text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded max-w-[120px] truncate">
                      <span className="whitespace-nowrap mr-1">Due:</span>
                      <IndianRupee className="h-3 w-3 mr-0.5 flex-shrink-0" />
                      <span className="truncate">{Number(appointment.due_amount).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    {getStatusBadge(appointment.order_status)}

                    <div className="flex space-x-2">
                      <a href={"tel:+91" + appointment.phone_number} >
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-[3rem] h-[2.5rem] p-0 border-gray-200 hover:bg-gray-50"
                        >
                          <Phone className="h-3 w-3 text-gray-600" />
                          <span className="sr-only">Call</span>
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-[3rem] h-[2.5rem] p-0 border-gray-200 hover:bg-gray-50"
                        onClick={() => handleNavigateLocation(formatFullAddress(appointment))}
                      >
                        <MapPin className="h-3 w-3 text-gray-600" />
                        <span className="sr-only">Map</span>
                      </Button>

                      <Button
                        size="sm"
                        asChild
                        className="bg-pranaam-red hover:bg-red-700 text-white text-xs px-3 w-[3rem] h-[2.5rem]"
                      >
                        <Link to={`/appointments/${appointment.order_id}`}>
                          View
                        </Link>
                      </Button>
                    </div>

                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center text-gray-500 text-sm">
              No {filter} appointments.
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default AppointmentsScreen;
